import { DOCUMENT } from '@angular/common';
import { computed, effect, inject, Injectable, signal } from '@angular/core';

export type ThemeMode = 'system' | 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeModeService {
  private readonly document = inject(DOCUMENT);
  private readonly storageKey = 'db_console_theme';
  private readonly sharedCookieKey = 'iam_theme_mode';
  private readonly mediaQuery = this.getMediaQuery();
  private readonly prefersDark = signal<boolean>(this.mediaQuery?.matches ?? false);

  readonly mode = signal<ThemeMode>(this.resolveInitialMode());
  readonly isDark = computed(() => this.mode() === 'dark' || (this.mode() === 'system' && this.prefersDark()));

  constructor() {
    this.mediaQuery?.addEventListener('change', (event) => {
      this.prefersDark.set(event.matches);
    });

    effect(() => {
      const mode = this.mode();
      const isDark = this.isDark();
      this.applyMode(mode, isDark);
      this.persistMode(mode);
    });
  }

  cycleMode(): void {
    this.mode.update((mode) => {
      if (mode === 'system') {
        return 'dark';
      }

      if (mode === 'dark') {
        return 'light';
      }

      return 'system';
    });
  }

  private resolveInitialMode(): ThemeMode {
    const sharedMode = this.readCookieMode();
    if (sharedMode) {
      return sharedMode;
    }

    const stored = this.readStoredMode();
    if (stored) {
      return stored;
    }

    return 'system';
  }

  private readStoredMode(): ThemeMode | null {
    try {
      const value = globalThis.localStorage?.getItem(this.storageKey);
      return this.parseMode(value);
    } catch {
      return null;
    }
  }

  private persistMode(mode: ThemeMode): void {
    try {
      globalThis.localStorage?.setItem(this.storageKey, mode);
      this.writeCookieMode(mode);
    } catch {
      return;
    }
  }

  private applyMode(mode: ThemeMode, isDark: boolean): void {
    const root = this.document.documentElement;
    root.classList.toggle('dark', isDark);
    root.dataset['theme'] = mode;
    root.dataset['resolvedTheme'] = isDark ? 'dark' : 'light';
  }

  private readCookieMode(): ThemeMode | null {
    const cookie = (this.document.cookie ?? '')
      .split(';')
      .map((item) => item.trim())
      .find((item) => item.startsWith(`${this.sharedCookieKey}=`));

    if (!cookie) {
      return null;
    }

    return this.parseMode(decodeURIComponent(cookie.split('=').slice(1).join('=')));
  }

  private writeCookieMode(mode: ThemeMode): void {
    const encodedMode = encodeURIComponent(mode);
    const maxAge = 60 * 60 * 24 * 365;
    const secure = globalThis.location?.protocol === 'https:' ? '; Secure' : '';
    const baseCookie = `${this.sharedCookieKey}=${encodedMode}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;

    this.document.cookie = baseCookie;

    const sharedDomain = this.resolveSharedCookieDomain(globalThis.location?.hostname ?? '');
    if (sharedDomain) {
      this.document.cookie = `${baseCookie}; Domain=${sharedDomain}`;
    }
  }

  private parseMode(value: string | null): ThemeMode | null {
    return value === 'system' || value === 'dark' || value === 'light' ? value : null;
  }

  private resolveSharedCookieDomain(hostname: string): string | null {
    if (!hostname || hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return null;
    }

    const parts = hostname.split('.');
    if (parts.length < 2) {
      return null;
    }

    const secondLevelCountryDomains = new Set(['ac.id', 'co.id', 'go.id', 'or.id', 'sch.id', 'web.id']);
    const lastTwo = parts.slice(-2).join('.');
    const sliceCount = secondLevelCountryDomains.has(lastTwo) && parts.length >= 3 ? 3 : 2;

    return `.${parts.slice(-sliceCount).join('.')}`;
  }

  private getMediaQuery(): MediaQueryList | null {
    try {
      return globalThis.matchMedia?.('(prefers-color-scheme: dark)') ?? null;
    } catch {
      return null;
    }
  }
}
